import { children, createEffect, createMemo, createSignal, createUniqueId, Show, useContext } from 'solid-js';
import { Navigate } from '@solidjs/router';
import { Link, Meta, Title } from '@solidjs/meta';
import { Container, Heading, Picture, Section, Grid, GridThird, GridTwoThirds, Dialog, DialogContent } from '@troon/ui';
import dayjs, { timeFrameNameFromHours } from '@troon/dayjs';
import { clientOnly } from '@solidjs/start';
import { createSearchStore } from '@troon/primitives/search-store';
import { useTrackEvent } from '@troon/analytics';
import { useUtmParams } from '@troon/primitives/utm';
import { getTeeTimeSearchSchema } from '@troon/tee-times/schema';
import { FacilityCtx } from '../../../../providers/facility';
import { getSchemaString } from '../../../../modules/schema/schema';
import { breadcrumbs } from '../../../../modules/schema/breadcrumb-list';
import { image } from '../../../../modules/schema/image';
import { webpage } from '../../../../modules/schema/webpage';
import { TeeTimeSearchHeader } from '../../../../components/tee-time-search-header';
import { TeeTimeFilters, TeeTimeFiltersFAB } from '../../../../components/tee-time-search-filters';
import { ClosureMessage } from '../../../../components/closure-message';
import { FacilityHeaderButtons } from '../../../../components/facility/header-buttons';
import { FacilityBrand } from '../../../../graphql';
import { useUser } from '../../../../providers/user';
import { TeeTimeAlertCallout } from '../../../../components/upsells/tee-time-alert-callout';
import { TeeTimeAlertButton } from '../../../../components/tee-time-alert-button';
import { TeeTimeAlertForm } from '../../../../partials/tee-time-alerts';
import { TeeTimeNoResultsAlert } from '../../../../components/tee-time-no-results-alert';
import { usePersisted } from '../../../../providers/persistence-store';
import { SupportBadge } from '../../../../components/support-badge';
import { UpsellAccessMinimal } from '../../../../components/upsells/access-minimal';
import type { ComponentProps } from 'solid-js';
import type { RouteDefinition } from '@solidjs/router';

const TeeTimeResults = clientOnly(async () => ({
	default: (await import('@troon/tee-times/results')).TeeTimeResults,
}));

export default function ReserveTeeTime() {
	const facility = useContext(FacilityCtx);
	const utm = useUtmParams();
	const user = useUser();
	const [filters, setFilters] = createSearchStore(getTeeTimeSearchSchema());
	const trackEvent = useTrackEvent();
	const [persisted] = usePersisted();

	const [teeTimeAlertRange, setTeeTimeAlertRange] = createSignal<null | [number, number]>(null);
	const teeTimeAlertDialogId = createUniqueId();
	createEffect(() => {
		// Manually triggered dialogs that don't use DialogTrigger must call dialogOpen
		if (teeTimeAlertRange()) {
			trackEvent('dialogOpened', { key: 'tee-time-alert-inline' });
		}
	});

	const showAccessInterstitial = createMemo(() => {
		return dayjs().isAfter(dayjs(persisted.seenAccessInterstitial?.expires ?? new Date(2020, 11, 1)));
	});

	const respectDisplayConfig = createMemo(
		() =>
			!user()?.troonAccessProductType && utm().campaign === 'course-booking-link' && utm().source === facility?.slug,
	);

	const teeTimes = children(() => (
		<>
			<TeeTimeResults
				filterByCourse
				facilities={[facility!]}
				filters={filters}
				setFilters={setFilters}
				singleFacility
				teeTimeHref={(teeTime, params) => {
					const taPlusRate = teeTime.rates.find((r) => r.rateType === 'TROON_ACCESS_PLUS');
					// Experiment to swap to the TA+ rate if available so that the modal interstitial is presented
					if (
						(!respectDisplayConfig() || facility?.displayConfig.includes('RESERVE_ACCESS_RATE')) &&
						showAccessInterstitial() &&
						taPlusRate
					) {
						params.set('rateId', taPlusRate.id);
					}
					return `/course/${teeTime.facility.slug}/reserve-tee-time/${teeTime.id}?${params.toString()}`;
				}}
				includeFeaturedOffers={
					!respectDisplayConfig()
						? facility?.supportsTroonAccess
						: facility?.displayConfig.includes('FEATURED_ACCESS_OFFERS')
				}
				includeInlineOffers={
					!respectDisplayConfig()
						? facility?.supportsTroonAccess
						: facility?.displayConfig.includes('INLINE_ACCESS_OFFERS')
				}
				includeUpsellBanner={
					!respectDisplayConfig()
						? facility?.supportsTroonAccess
						: facility?.displayConfig.includes('INLINE_ACCESS_UPSELL')
				}
				upsellBanner={
					(!respectDisplayConfig() && facility?.supportsTroonAccess) ||
					facility?.displayConfig.includes('INLINE_ACCESS_UPSELL') ? (
						<UpsellAccessMinimal location="single-course tee time search" />
					) : null
				}
				userAccessProductType={
					user()?.troonAccessProductType as ComponentProps<typeof TeeTimeResults>['userAccessProductType']
				}
				alertRenderer={(item) => (
					<TeeTimeAlertButton
						aria-haspopup="dialog"
						aria-controls={teeTimeAlertRange() ? teeTimeAlertDialogId : undefined}
						aria-expanded={!!teeTimeAlertRange()}
						onClick={() => setTeeTimeAlertRange([item().startAt, item().endAt])}
					>
						{item().content}
					</TeeTimeAlertButton>
				)}
				noResultsContent={() => <TeeTimeNoResultsAlert facilities={[facility!]} filters={filters} />}
			/>
			<Dialog
				id={teeTimeAlertDialogId}
				key="tee-time-alert-inline"
				open={!!teeTimeAlertRange()}
				onOpenChange={(open) => {
					if (!open) {
						setTeeTimeAlertRange(null);
					}
				}}
			>
				<DialogContent header="Create a Tee Time Alert" headerLevel="h3" height="fit">
					<TeeTimeAlertForm
						facilities={facility ? [facility] : []}
						selectedDay={filters.date}
						selectedTime={teeTimeAlertRange()!}
						selectedPlayers={filters.players}
						courseSlugs={facility?.courses.map((c) => c.slug)}
						onCancel={() => setTeeTimeAlertRange(null)}
						onSaved={() => setTeeTimeAlertRange(null)}
					/>
				</DialogContent>
			</Dialog>
		</>
	));

	return (
		<Show
			when={facility?.isBookable && facility}
			fallback={<Navigate href={facility ? `/course/${facility?.slug}` : ''} />}
		>
			{(facility) => (
				<>
					<Title>{`${facility().name} | Book tee times | Golf with Access`}</Title>
					<Meta name="description" content={`Reserve golf tee times at ${facility().name} and earn Access Points.`} />
					<Link rel="canonical" href={`/course/${facility().slug}/reserve-tee-time?payMode=${filters.payMode}`} />
					<script
						type="application/ld+json"
						innerText={getSchemaString([
							breadcrumbs(`/course/${facility().slug}/reserve-tee-time`, [
								{ name: 'Home', pathname: '/' },
								{ name: 'Tee Times', pathname: '/tee-times' },
								{ name: facility().name ?? '', pathname: `/course/${facility().slug}` },
								{ name: 'Reserve tee times', pathname: `/course/${facility().slug}/reserve-tee-time` },
							]),
							facility().hero ? image(facility().hero!) : undefined,
							webpage(`/course/${facility().slug}`, {
								title: `${facility().name} | Reserve tee times | Golf with Access`,
							}),
						])}
					/>

					<div class="-mb-6 grow bg-neutral-100 md:-mb-12">
						<div class="bg-white">
							<Container class="flex-row flex-wrap items-center gap-3 pt-6 md:gap-8">
								<div class="order-1 flex aspect-square shrink-0 grow-0 basis-20 rounded-md border border-neutral bg-white p-2 md:basis-24 md:p-2">
									<Picture
										preload
										src={facility().logo}
										width={200}
										height={200}
										sizes="(min-width: 768px) 8rem, 4rem"
										mode="contain"
										class="w-full max-w-max rounded-lg object-cover"
									/>
								</div>
								<div class="order-3 flex shrink-0 grow basis-2/3 flex-col justify-around gap-2 sm:order-2 sm:basis-1/2">
									<div class="flex flex-col">
										<Heading as="h1" size="h4">
											{facility().name}
										</Heading>
										<p class="order-first text-sm tracking-widest text-neutral-800 uppercase">
											<Show when={facility().brand === FacilityBrand.TroonPartner}>Troon Partner Course</Show>
										</p>
										<Show when={facility().address}>
											{(address) => (
												<address
													class="text-sm text-neutral-800 not-italic"
													aria-label={`Address for ${facility().name}`}
												>
													{[
														address().street,
														address().city,
														[address().state, address().postalCode].filter(Boolean).join(' '),
													]
														.filter(Boolean)
														.join(', ')}
												</address>
											)}
										</Show>
									</div>
									<SupportBadge points={facility().supportsAccessPoints} rates={facility().supportsTroonAccess} />
								</div>
								<div class="order-2 flex grow justify-end gap-1 text-neutral-800 sm:order-3">
									<FacilityHeaderButtons facility={facility()} />
								</div>
							</Container>
						</div>

						<TeeTimeSearchHeader
							class="border-b border-neutral"
							trackQuery={false}
							maxDate={dayjs()
								.tz(facility().timezone)
								.add(Math.max(...(facility()?.courses.flatMap((course) => course.bookingWindowInDays) ?? [14])), 'days')
								.toDate()}
							hideQueryInput
							filters={filters}
							setFilters={setFilters}
							scrollHeader={
								<div class="@container flex gap-2">
									<div class="hidden aspect-square shrink-0 grow-0 basis-16 rounded-md border border-neutral bg-white p-1 @xs:flex">
										<Picture
											preload
											src={facility().logo}
											width={200}
											height={200}
											sizes="(min-width: 768px) 8rem, 4rem"
											mode="contain"
											class="w-full max-w-max rounded-lg object-cover"
										/>
									</div>
									<div class="flex grow flex-col justify-center truncate">
										<Heading as="p" size="h4" class="truncate">
											{facility().name}
										</Heading>
										<p class="truncate text-sm text-neutral-800">
											{dayjs(filters.date).format('ddd, MMM D')} • {filters.players} player
											{filters.players > 1 ? 's' : ''} • {timeFrameNameFromHours(filters.startAt, filters.endAt, true)}
										</p>
									</div>
								</div>
							}
						/>

						<Container>
							<Section class="pt-4 lg:pt-8">
								<ClosureMessage
									notes={facility().notes}
									closures={facility().closures}
									date={filters.date}
									timezone={facility().timezone}
								/>
								<Show
									when={
										(respectDisplayConfig()
											? (facility().displayConfig.includes('GOLFNOW_OFFER_FILTER') && facility().supportsHotDeals) ||
												(facility().displayConfig.includes('ACCESS_FILTER') && facility().supportsTroonAccess)
											: facility().supportsTroonAccess || facility().supportsHotDeals) || facility().courses?.length > 1
									}
									fallback={teeTimes()}
								>
									<Grid class="gap-y-0">
										<GridThird>
											<div class="sticky top-54 hidden flex-col gap-y-8 pb-8 lg:flex xl:top-34">
												<div
													// Manual top calculations are depend on the height of the TeeTimeSearchFields section
													class="flex max-h-[calc(100dvh-15rem)] flex-col gap-6 overflow-auto rounded-lg border border-neutral bg-white p-6 xl:max-h-[calc(100dvh-11rem)]"
												>
													<Heading as="h2" size="h5">
														Filter by
													</Heading>

													<TeeTimeFilters
														courses={facility().courses}
														filters={filters}
														setFilters={setFilters}
														withAccessPlusOffers={facility().supportsTroonAccess && !respectDisplayConfig()}
														withGolfnowOffers={
															respectDisplayConfig()
																? facility().displayConfig.includes('GOLFNOW_OFFER_FILTER')
																: facility().supportsHotDeals
														}
													/>
												</div>
												<Show when={facility().courses.some((c) => c.supportsTeeTimeAlerts)}>
													<TeeTimeAlertCallout
														// @ts-expect-error TODO: need to update multi-course search to use a REST endpoint
														facilities={[facility]}
														selectedDay={filters.date}
														selectedTime={[filters.startAt, filters.endAt]}
														selectedPlayers={filters.players}
														courseSlugs={
															filters.filterFacilities
																? Array.isArray(filters.filterFacilities)
																	? filters.filterFacilities
																	: [filters.filterFacilities]
																: undefined
														}
													/>
												</Show>
											</div>
										</GridThird>
										<GridTwoThirds>
											{teeTimes()}
											<Show when={facility().courses.some((c) => c.supportsTeeTimeAlerts)}>
												<div class="lg:hidden">
													<TeeTimeAlertCallout
														// @ts-expect-error TODO: need to update multi-course search to use a REST endpoint
														facilities={[facility]}
														selectedDay={filters.date}
														selectedTime={[filters.startAt, filters.endAt]}
														selectedPlayers={filters.players}
														courseSlugs={
															filters.filterFacilities
																? Array.isArray(filters.filterFacilities)
																	? filters.filterFacilities
																	: [filters.filterFacilities]
																: undefined
														}
													/>
												</div>
											</Show>
										</GridTwoThirds>
									</Grid>
									<div class="lg:hidden">
										<TeeTimeFiltersFAB
											courses={facility().courses}
											filters={filters}
											setFilters={setFilters}
											withAccessPlusOffers={facility().supportsTroonAccess && !respectDisplayConfig()}
											withGolfnowOffers={
												respectDisplayConfig()
													? facility().displayConfig.includes('GOLFNOW_OFFER_FILTER')
													: facility().supportsHotDeals
											}
										/>
									</div>
								</Show>
							</Section>
						</Container>
					</div>
				</>
			)}
		</Show>
	);
}

export const route = { info: { nav: { sticky: false } } } satisfies RouteDefinition;
